import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/api/v1' || 'http://localhost:4000/api/v1';

class ChatbotService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000, // Reduced from 30000 to 10000ms
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        // Try to get token from multiple sources
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else if (error.response?.status === 429) {
          // Rate limit exceeded
          const errorMessage = error.response.data?.message || 'Too many requests. Please try again later.';
          throw new Error(errorMessage);
        }
        return Promise.reject(error);
      }
    );
  }

  async sendMessage(message, sessionId = null) {
    try {
      const payload = { message };
      if (sessionId) {
        payload.sessionId = sessionId;
      }

      const response = await this.api.post('/chatbot/message', payload);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
      }
      
      throw error;
    }
  }

  async getSessions() {
    try {
      const response = await this.api.get('/chatbot/sessions');
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get sessions');
      }
    } catch (error) {
      console.error('Error getting sessions:', error);
      throw error;
    }
  }

  async getSessionMessages(sessionId) {
    try {
      const response = await this.api.get(`/chatbot/sessions/${sessionId}/messages`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get session messages');
      }
    } catch (error) {
      console.error('Error getting session messages:', error);
      throw error;
    }
  }

  async deleteSession(sessionId) {
    try {
      const response = await this.api.delete(`/chatbot/sessions/${sessionId}`);
      
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  async clearSession(sessionId) {
    return this.deleteSession(sessionId);
  }

  async getQuickQuestions() {
    try {
      // For now, return static questions. In a real implementation,
      // this could be an API call that generates contextual questions
      return [
        "How do I join a live session?",
        "What courses are available?",
        "How do I reset my password?",
        "Where can I find course materials?",
        "How do I contact support?",
        "What are the system requirements?",
        "How do I download certificates?",
        "Where can I view my progress?"
      ];
    } catch (error) {
      console.error('Error getting quick questions:', error);
      return [];
    }
  }

  async getTokenUsage() {
    try {
      // This would typically come from an API endpoint
      // For now, return mock data based on localStorage
      const stored = localStorage.getItem('chatbot_token_usage');
      if (stored) {
        return JSON.parse(stored);
      }
      
      return {
        used: 0,
        limit: 20000,
        resetDate: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting token usage:', error);
      return {
        used: 0,
        limit: 20000,
        resetDate: new Date().toISOString()
      };
    }
  }

  async updateTokenUsage(usage) {
    try {
      localStorage.setItem('chatbot_token_usage', JSON.stringify(usage));
    } catch (error) {
      console.error('Error updating token usage:', error);
    }
  }

  // Admin methods
  async uploadKnowledge(title, content) {
    try {
      const response = await this.api.post('/admin/knowledge', { title, content });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to upload knowledge');
      }
    } catch (error) {
      console.error('Error uploading knowledge:', error);
      throw error;
    }
  }

  async getAllKnowledge() {
    try {
      const response = await this.api.get('/admin/knowledge');
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get knowledge');
      }
    } catch (error) {
      console.error('Error getting knowledge:', error);
      throw error;
    }
  }

  async updateKnowledge(id, title, content) {
    try {
      const response = await this.api.put(`/admin/knowledge/${id}`, { title, content });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update knowledge');
      }
    } catch (error) {
      console.error('Error updating knowledge:', error);
      throw error;
    }
  }

  async deleteKnowledge(id) {
    try {
      const response = await this.api.delete(`/admin/knowledge/${id}`);
      
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete knowledge');
      }
    } catch (error) {
      console.error('Error deleting knowledge:', error);
      throw error;
    }
  }

  async getKnowledgeStats() {
    try {
      const response = await this.api.get('/admin/knowledge/stats');
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get knowledge stats');
      }
    } catch (error) {
      console.error('Error getting knowledge stats:', error);
      throw error;
    }
  }

  // Utility methods
  generateMessageHash(message) {
    // Simple hash function for message caching
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      const char = message.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  validateMessage(message) {
    if (!message || typeof message !== 'string') {
      return { valid: false, error: 'Message is required' };
    }
    
    if (message.trim().length === 0) {
      return { valid: false, error: 'Message cannot be empty' };
    }
    
    if (message.length > 500) {
      return { valid: false, error: 'Message is too long (max 500 characters)' };
    }
    
    return { valid: true };
  }

  sanitizeMessage(message) {
    return message
      .trim()
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>]/g, '') // Remove remaining angle brackets
      .substring(0, 500); // Ensure max length
  }
}

export const chatbotService = new ChatbotService();
export default chatbotService;
